import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, Users, Calendar, Activity, Ruler, LogOut, KeyRound } from "lucide-react";
import Logo from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import NotificationBell from "@/components/NotificationBell";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";

const NAV = [
  { label: "Panelim", to: "/trainer", icon: LayoutDashboard, end: true },
  { label: "Üyelerim", to: "/trainer/members", icon: Users },
  { label: "Takvimim", to: "/trainer/calendar", icon: Calendar },
  { label: "Ölçümler", to: "/trainer/measurements", icon: Ruler },
  { label: "Antrenmanlar", to: "/trainer/workouts", icon: Activity, phase: 3 },
];

export default function TrainerLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const initials = (user?.name || "T").split(" ").map((w) => w[0]).slice(0, 2).join("");
  const doLogout = async () => { await logout(); navigate("/login"); };

  return (
    <div className="min-h-screen bg-[#0B0B0E]">
      <header className="h-16 sticky top-0 z-20 bg-[#0E0E14]/90 backdrop-blur-xl border-b border-[#1A1A24] flex items-center justify-between px-4 sm:px-6">
        <Logo />
        <nav className="hidden md:flex items-center gap-1" data-testid="trainer-navigation">
          {NAV.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink key={item.to} to={item.to} end={item.end}
                data-testid={`trainer-nav-${item.to.split("/").pop() || "dashboard"}`}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-3.5 py-2 rounded-xl text-sm font-medium transition-all ${
                    isActive ? "bg-[#FF5500] text-white" : "text-[#A0A0B2] hover:text-white hover:bg-[#1B1B27]"
                  }`
                }>
                <Icon size={17} /> {item.label}
                {item.phase && <span className="text-[9px] font-bold px-1 py-0.5 rounded bg-[#F59E0B]/15 text-[#F59E0B]">F{item.phase}</span>}
              </NavLink>
            );
          })}
        </nav>
        <div className="flex items-center gap-2">
          <NotificationBell role={user?.role} />
          <ChangePasswordDialog trigger={<button data-testid="trainer-change-password" title="Şifre Değiştir" className="p-2 rounded-lg text-[#68687B] hover:text-[#FF5500] hover:bg-[#1B1B27]"><KeyRound size={18} /></button>} />
          <Avatar className="w-9 h-9"><AvatarFallback className="bg-[#FF5500] text-white text-xs font-bold">{initials}</AvatarFallback></Avatar>
          <button onClick={doLogout} data-testid="trainer-logout-button" className="p-2 rounded-lg text-[#68687B] hover:text-[#EF4444] hover:bg-[#1B1B27]"><LogOut size={18} /></button>
        </div>
      </header>

      {/* Mobile tab bar */}
      <nav className="md:hidden flex items-center gap-1 px-3 py-2 border-b border-[#1A1A24] overflow-x-auto bg-[#0E0E14]">
        {NAV.map((item) => (
          <NavLink key={item.to} to={item.to} end={item.end}
            className={({ isActive }) => `flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium ${isActive ? "bg-[#FF5500] text-white" : "text-[#A0A0B2] bg-[#13131C]"}`}>
            <item.icon size={14} /> {item.label}
          </NavLink>
        ))}
      </nav>

      <main className="p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto animate-fade-up">
        <Outlet />
      </main>
    </div>
  );
}
